import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../store';
import { firestore, parseDoc, functions } from '../firebase';
import { User, UserGender, UserPhoto, UserStatus } from '../types';
import { keyBy } from 'lodash';

interface UsersState {
  users: { [ id: string ]: User };
  profilePhotos: { [ id: string ]: UserPhoto[] };
  cursor: any;
  listLoading: boolean;
  detailsLoading: { [ userId: string ]: boolean };
  photosLoading: boolean;
  deletePending: string | null;
  disablePending: string | null;
}

const initialState: UsersState = {
  users: {},
  profilePhotos: {},
  listLoading: false,
  detailsLoading: {},
  photosLoading: false,
  cursor: null,
  deletePending: null,
  disablePending: null,
}


const service = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setListLoading(state, action: PayloadAction<boolean>): UsersState {
      return { ...state, listLoading: action.payload, }
    },
    setDetailsLoading(state, action: PayloadAction<{ userId: string, loading: boolean }>): UsersState {
      return {
        ...state,
        detailsLoading: {
          ...state.detailsLoading,
          [ action.payload.userId ]: action.payload.loading,
        },
      }
    },
    setPhotosLoading(state, action: PayloadAction<boolean>): UsersState {
      return { ...state, photosLoading: action.payload, }
    },
    setDeletePending(state, action: PayloadAction<string | null>): UsersState {
      return { ...state, deletePending: action.payload, }
    },
    setDisablePending(state, action: PayloadAction<string | null>): UsersState {
      return { ...state, disablePending: action.payload, }
    },
    setCursor(state, action: PayloadAction<string | null>): UsersState {
      return { ...state, cursor: action.payload, }
    },
    setList(state, action: PayloadAction<{ users: User[], cursor: any }>): UsersState {
      const users = keyBy(action.payload.users, 'id');
      return {
        ...state,
        users: {
          ...users,
        },
        cursor: action.payload.cursor,
      }
    },
    appendToList(state, action: PayloadAction<{ users: User[], cursor: any }>): UsersState {
      const users = keyBy(action.payload.users, 'id');
      return {
        ...state,
        users: {
          ...state.users,
          ...users,
        },
        cursor: action.payload.cursor,
      }
    },
    setUserDetails(state, action: PayloadAction<User>): UsersState {
      return {
        ...state, users: {
          ...state.users,
          [ action.payload.id ]: { ...action.payload },
        },
      }
    },

    setProfilePhotos(state, action: PayloadAction<{ id: string, photos: UserPhoto[] }>): UsersState {
      return {
        ...state,
        profilePhotos: {
          ...state.profilePhotos,
          [ action.payload.id ]: [ ...action.payload.photos ],
        },
      }
    },
    updateUserStatus(state, action: PayloadAction<{ userId: string, status: UserStatus }>): UsersState {
      return {
        ...state,
        users: {
          ...state.users,
          [ action.payload.userId ]: {
            ...state.users[ action.payload.userId ],
            status: action.payload.status,
          }
        },
      }
    },

  }
});


interface LoadListArgs {
  gender?: UserGender | 'all';
  status?: UserStatus | 'all';
  sortBy?: 'createTime' | 'lastActiveTimeInternal';
  loadMore?: boolean;
}


let oldArgs: LoadListArgs | null = null;

const didArgsChange = (newArgs: LoadListArgs) => {
  if (!oldArgs) {
    return true;
  }

  return oldArgs.gender !== newArgs.gender
    || oldArgs.sortBy !== newArgs.sortBy
    || oldArgs.status !== newArgs.status;
}

const loadList = (args: LoadListArgs): AppThunk => async (dispatch, getState) => {


  const state = getState().users;

  const filtersChanged = didArgsChange(args);

  if (!filtersChanged && !args.loadMore) {
    return;
  }

  dispatch(service.actions.setListLoading(true));


  if (filtersChanged) {
    dispatch(service.actions.setList({
      users: [],
      cursor: null,
    }));
  }

  oldArgs = { ...args };

  let query = firestore.collection('users').limit(20);

  if (args.gender && args.gender !== 'all') {
    query = query.where('gender', '==', args.gender)
  }
  if (args.status && args.status !== 'all') {
    query = query.where('status', '==', args.status)
  }

  if (!args.sortBy || args.sortBy === 'createTime') {
    query = query.orderBy('createTime', 'desc');
  }
  else {
    query = query.orderBy('lastActiveTimeInternal', 'desc');
  }


  if (state.cursor && args.loadMore) {
    query = query.startAfter(state.cursor);
  }

  const result = await query.get();


  const list: User[] = [];
  let cursor: any = null;

  for (const doc of result.docs) {
    const user = parseDoc<User>(doc);
    user.id = doc.id;
    list.push(user);
    cursor = doc;
  }

  if (filtersChanged) {
    dispatch(service.actions.setList({
      users: list,
      cursor: cursor,
    }));
  }
  else {
    dispatch(service.actions.appendToList({
      users: list,
      cursor: cursor,
    }));
  }

  dispatch(service.actions.setListLoading(false));

  console.log(list);
}

const loadUserDetails = (userId: string): AppThunk => async (dispatch, getState) => {

  const state = getState();
  if (state.users.detailsLoading[ userId ] || state.users.users[ userId ]) {
    return;
  }

  console.log('load user details', userId);

  dispatch(service.actions.setDetailsLoading({
    userId,
    loading: true
  }));
  const doc = await firestore.collection('users').doc(userId).get();

  if (!doc.exists) {
    return;
  }

  const user = doc.data() as User;
  user.id = doc.id;
  dispatch(service.actions.setUserDetails(user));
  dispatch(service.actions.setDetailsLoading({
    userId,
    loading: false,
  }));
}

const loadProfilePhotos = (userId: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setPhotosLoading(true));
  const result = await firestore
    .collection('users')
    .doc(userId)
    .collection('profile_photos')
    .orderBy('order', 'asc')
    .get();


  const photos: UserPhoto[] = [];

  for (const doc of result.docs) {
    const photo = parseDoc<UserPhoto>(doc);
    photos.push(photo);
  }

  dispatch(service.actions.setProfilePhotos({
    id: userId,
    photos,
  }));

  dispatch(service.actions.setPhotosLoading(false));
}




const deleteUser = (userId: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setDeletePending(userId));

  const func = functions.httpsCallable('adminDeleteUser');

  await func({ userId });

  dispatch(service.actions.updateUserStatus({
    userId,
    status: 'deleted',
  }));

  dispatch(service.actions.setDeletePending(null));
}

const disableUser = (userId: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setDisablePending(userId));

  const func = functions.httpsCallable('adminDisableUser');

  await func({ userId });

  dispatch(service.actions.updateUserStatus({
    userId,
    status: 'disabled',
  }));

  dispatch(service.actions.setDisablePending(null));
}



export const actions = {
  loadList,
  loadUserDetails,
  loadProfilePhotos,
  deleteUser,
  disableUser,
  updateUserStatus: service.actions.updateUserStatus,
}

export default service.reducer;